import React from 'react';
import { Trans } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField, IconButton, Tooltip, Button as MuiButton } from '@mui/material';
import {
  SwapVert as SwapIcon,
} from '@mui/icons-material';
import {
  set, get, cloneDeep, isEmpty, map, isArray, compact, flatten, values, keys
} from 'lodash';
import APIService from '../../services/APIService';
import GAService from '../../services/GAService';
import { arrayToObject, toParentURI, URIToParentParams } from '../../common/utils';
import { fetchMapTypes } from './utils';
import { OperationsContext } from '../app/LayoutContext';
import FormComponent, { CardSection } from '../common/FormComponent'
import { ID_REGEX, NUM_REGEX } from '../../common/constants'
import {
  required, matchPattern
} from '../../common/validators';
import Breadcrumbs from '../common/Breadcrumbs'
import CustomAttributesForm from '../common/CustomAttributesForm'
import CloseIconButton from '../common/CloseIconButton';
import Button from '../common/Button'
import ConceptSearchAutocomplete from '../common/ConceptSearchAutocomplete'
import SourceSearchAutocomplete from '../common/SourceSearchAutocomplete'

const ANCHOR_UNDERLINE_STYLES = {textDecoration: 'underline', cursor: 'pointer'}
const OPTIONAL_BLANK_FIELDS = [
  'id', 'external_id', 'from_concept_url', 'from_concept_code', 'from_concept_name', 'from_source_url', 'from_source_version',
  'to_concept_url', 'to_concept_code', 'to_concept_name', 'to_source_url', 'to_source_version'
]

class MappingForm extends FormComponent {
  static contextType = OperationsContext;

  constructor(props) {
    super(props);

    const mandatoryFieldStruct = this.getMandatoryFieldStruct()
    const fieldStruct = this.getFieldStruct()
    const autoAssignedId = Boolean(props.source?.autoid_mapping_mnemonic)

    this.state = {
      manualMnemonic: false,
      manualExternalId: false,
      advanced: Boolean(props.edit || props.copyFrom),
      fromConcept: null,
      toConcept: null,
      fromSource: null,
      toSource: null,
      mapTypes: [],
      parent: null,
      fields: {
        id: {...mandatoryFieldStruct, validators: autoAssignedId ? [matchPattern(ID_REGEX)] : [required(), matchPattern(ID_REGEX)]},
        map_type: {...mandatoryFieldStruct},
        external_id: {...fieldStruct},
        from_concept_url: {...fieldStruct},
        from_concept_code: {...fieldStruct},
        from_concept_name: {...fieldStruct},
        from_source_url: {...fieldStruct},
        from_source_version: {...fieldStruct},
        to_concept_url: {...fieldStruct},
        to_concept_code: {...fieldStruct},
        to_concept_name: {...fieldStruct},
        to_source_url: {...fieldStruct},
        to_source_version: {...fieldStruct},
        comment: {...(props.edit ? mandatoryFieldStruct : fieldStruct)},
        extras: [],
        sort_weight: {...fieldStruct, validators: [matchPattern(NUM_REGEX)]}
      }
    }
  }

  componentDidMount() {
    fetchMapTypes(data => this.setState({mapTypes: data}))
    if(this.props.edit && this.props.mapping)
      this.setFieldsForEdit()
    else if(this.props.copyFrom)
      this.fetchMappingToCreate()
    if(this.props.selectedConcepts)
      this.setFieldsFromSelectedConcepts()
    if(!this.props.edit)
      this.setState(state => ({parent: this.props.source, fromSource: state.fromSource || this.props.source || null}))
  }

  buildSource = (url, name) => {
    if(!url)
      return null
    if(this.props.source?.url === url)
      return this.props.source
    const params = URIToParentParams(url)
    return {
      url: url,
      id: params?.repo || url,
      short_code: params?.repo,
      name: name || params?.repo || url,
      owner: params?.owner,
      owner_type: params?.ownerType
    }
  }

  sourceFromConcept = concept => concept ?
                                 this.buildSource(concept.source_url || (concept.url ? toParentURI(concept.url) : ''), concept.source) :
                                 null

  fetchMappingToCreate = () => {
    APIService.new().overrideURL(this.props.copyFrom.url).get().then(response => this.setFieldsForEdit(response.data))
  }

  setFieldsForEdit(instanceOverride) {
    const { mapping, edit, copyFrom } = this.props;
    const instance = instanceOverride || (edit ? mapping : copyFrom)
    const attrs = [
      'id', 'map_type',
      'from_concept_url', 'from_concept_name',
      'from_source_url', 'from_source_version',
      'to_concept_url', 'to_concept_name',
      'to_source_url', 'to_source_version', 'sort_weight'
    ]
    if(!copyFrom?.id) {
      attrs.push('external_id')
    }
    const newState = {...this.state}
    attrs.forEach(attr => set(newState.fields, `${attr}.value`, get(instance, attr, '') || ''))
    newState.fields.to_concept_code.value = instance.to_concept_code ? decodeURIComponent(instance.to_concept_code) : instance.to_concept_code
    newState.fields.from_concept_code.value = instance.from_concept_code ? decodeURIComponent(instance.from_concept_code) : instance.from_concept_code
    if(!edit)
      newState.fields.id.value = ''
    newState.fields.extras = isEmpty(instance.extras) ? newState.fields.extras : map(instance.extras, (v, k) => ({key: k, value: v}))
    ;['from', 'to'].forEach(prefix => {
      const conceptURL = newState.fields[`${prefix}_concept_url`].value
      const sourceURL = newState.fields[`${prefix}_source_url`].value || (conceptURL ? toParentURI(conceptURL) : '')
      newState[this.getSourceStateKey(prefix)] = this.buildSource(sourceURL)
    })

    this.setState(newState);
  }

  setFieldsFromSelectedConcepts() {
    const { selectedConcepts } = this.props;
    const fromConcept = get(selectedConcepts, '0')
    const toConcept = get(selectedConcepts, '1')
    const newState = {...this.state}

    if(fromConcept) {
      newState.fromConcept = fromConcept
      newState.fromSource = this.sourceFromConcept(fromConcept)
      newState.fields.from_concept_url.value = fromConcept.url
      newState.fields.from_concept_name.value = fromConcept.display_name
    }
    if(toConcept) {
      newState.toConcept = toConcept
      newState.toSource = this.sourceFromConcept(toConcept)
      newState.fields.to_concept_url.value = toConcept.url
      newState.fields.to_concept_name.value = toConcept.display_name
    }
    this.setState(newState)
  }

  swapConcepts = () => {
    const fields = cloneDeep(this.state.fields)
    const newState = {...this.state}

    newState.fromConcept = this.state.toConcept
    newState.toConcept = this.state.fromConcept
    newState.fromSource = this.state.toSource
    newState.toSource = this.state.fromSource

    newState.fields.from_concept_url.value = fields.to_concept_url.value
    newState.fields.from_concept_code.value = fields.to_concept_code.value
    newState.fields.from_concept_name.value = fields.to_concept_name.value
    newState.fields.from_source_url.value = fields.to_source_url.value
    newState.fields.from_source_version.value = fields.to_source_version.value

    newState.fields.to_concept_url.value = fields.from_concept_url.value
    newState.fields.to_concept_code.value = fields.from_concept_code.value
    newState.fields.to_concept_name.value = fields.from_concept_name.value
    newState.fields.to_source_url.value = fields.from_source_url.value
    newState.fields.to_source_version.value = fields.from_source_version.value

    this.setState(newState)
  }

  getConceptStateKey = prefix => prefix === 'from' ? 'fromConcept' : 'toConcept'

  getSourceStateKey = prefix => prefix === 'from' ? 'fromSource' : 'toSource'

  onSourceSelect = (prefix, source) => {
    const newState = {...this.state}
    newState[this.getSourceStateKey(prefix)] = source || null
    newState[this.getConceptStateKey(prefix)] = null
    newState.fields[`${prefix}_source_url`] = {...newState.fields[`${prefix}_source_url`], errors: []}
    newState.fields[`${prefix}_concept_url`] = {...newState.fields[`${prefix}_concept_url`], value: '', errors: []}
    newState.fields[`${prefix}_concept_name`] = {...newState.fields[`${prefix}_concept_name`], value: ''}
    this.setState(newState)
  }

  onConceptSelect = (prefix, concept) => {
    const newState = {...this.state}
    newState[this.getConceptStateKey(prefix)] = concept || null
    newState.fields[`${prefix}_concept_url`] = {...newState.fields[`${prefix}_concept_url`], value: concept?.url || '', errors: []}
    newState.fields[`${prefix}_concept_name`] = {...newState.fields[`${prefix}_concept_name`], value: concept?.display_name || ''}
    this.setState(newState)
  }

  fillFieldsFromConcept = (newState, prefix) => {
    const concept = newState[this.getConceptStateKey(prefix)]
    const source = newState[this.getSourceStateKey(prefix)]
    if(!concept) {
      if(source)
        newState.fields[`${prefix}_source_url`].value = source.url || ''
      return
    }
    newState.fields[`${prefix}_concept_url`].value = concept.url || ''
    newState.fields[`${prefix}_concept_name`].value = concept.display_name || ''
    newState.fields[`${prefix}_concept_code`].value = concept.id || ''
    newState.fields[`${prefix}_source_url`].value = concept.source_url || (concept.url ? toParentURI(concept.url) : '') || source?.url || ''
  }

  resolveConceptFromFields = prefix => {
    const key = this.getConceptStateKey(prefix)
    const sourceKey = this.getSourceStateKey(prefix)
    const url = this.state.fields[`${prefix}_concept_url`].value
    const sourceURL = this.state.fields[`${prefix}_source_url`].value || (url ? toParentURI(url) : '')
    if(this.state[sourceKey]?.url !== sourceURL)
      this.setState({[sourceKey]: this.buildSource(sourceURL)})
    if(this.state[key]?.url === url)
      return
    this.setState({[key]: null})
    if(!url)
      return
    APIService.new().overrideURL(url).get().then(response => {
      if(response?.status === 200 && response?.data?.url === this.state.fields[`${prefix}_concept_url`].value)
        this.setState({[key]: response.data})
    })
  }

  toggleAdvanced = () => {
    if(this.state.advanced) {
      this.setState({advanced: false}, () => ['from', 'to'].forEach(this.resolveConceptFromFields))
      return
    }
    const newState = {...this.state}
    ;['from', 'to'].forEach(prefix => this.fillFieldsFromConcept(newState, prefix))
    newState.advanced = true
    this.setState(newState)
  }

  getIdHelperText() {
    const { parent, fields } = this.state
    const { edit, source, t } = this.props
    const id = fields.id.value || "[generated-mapping-id]"
    const parentURL = edit ? this.props.parentURL : get(parent, 'url');
    return (
      <span>
        {
          source.autoid_mapping_mnemonic &&
            <React.Fragment>
              <a onClick={this.toggleManualMnemonic} style={ANCHOR_UNDERLINE_STYLES}>{t('mapping.form.auto_assign')}</a><br/>
            </React.Fragment>
        }
        {
          source.autoid_mapping_mnemonic === 'sequential' &&
            <React.Fragment>
              <span>{t('mapping.form.auto_id_sequence_help')}</span><br/>
            </React.Fragment>
        }
        {
          source.autoid_mapping_mnemonic === 'uuid' &&
            <React.Fragment>
              <span>{t('mapping.form.auto_id_uuid_help')}</span><br/>
            </React.Fragment>
        }
        <span>{t('repo.version.form.id.helper_text')}</span>
        <br />
        {
          parentURL &&
            <span>
              <span>{t('mapping.form.mapping_url')} <br />
                { `${window.location.origin}/#${parentURL}mappings/` }
              </span>
              <span><b>{id}</b>/</span>
            </span>
        }
      </span>
    )
  }

  getExternalIdHelperText() {
    const { source, t } = this.props
    if(!source)
      return ''
    return (
      <span>
        {
          source.autoid_mapping_external_id &&
            <React.Fragment>
              <a onClick={this.toggleManualExternalId} style={ANCHOR_UNDERLINE_STYLES}>{t('mapping.form.auto_assign')}</a><br/>
            </React.Fragment>
        }
        {
          source.autoid_mapping_external_id === 'sequence' &&
            <span>{t('mapping.form.auto_id_sequence_help')}</span>
        }
        {
          source.autoid_mapping_external_id === 'uuid' &&
            <span>{t('mapping.form.auto_id_uuid_help')}</span>
        }
        {
          !source.autoid_mapping_external_id && ''
        }
      </span>
    )
  }

  onTextFieldChange = event => {
    this.setFieldValue(event.target.id, event.target.value)
  }

  onAutoCompleteChange = (id, item) => {
    this.setFieldValue(id, item?.id || '', true)
  }

  isConceptRefValid = prefix => {
    const { fields } = this.state
    const url = fields[`${prefix}_concept_url`].value
    const code = fields[`${prefix}_concept_code`].value
    const name = fields[`${prefix}_concept_name`].value
    return Boolean(url) || Boolean(code && name)
  }

  setConceptRefErrors = () => {
    const { advanced, fromSource, toSource } = this.state
    const { t } = this.props
    const sourceMessage = t('mapping.source_required')
    const conceptMessage = advanced ? t('mapping.concept_ref_required') : t('mapping.concept_required')
    // in the basic view the source has to be picked before the concept can be searched, so only
    // point at the field the user has to act on next instead of flagging both at once
    const validity = ['from', 'to'].map(prefix => {
      const sourceValid = advanced || Boolean(prefix === 'from' ? fromSource : toSource)
      const conceptValid = this.isConceptRefValid(prefix)
      return {prefix, sourceValid, conceptValid}
    })
    this.setState(state => {
      const newState = {...state, fields: {...state.fields}}
      validity.forEach(({prefix, sourceValid, conceptValid}) => {
        newState.fields[`${prefix}_source_url`] = {
          ...newState.fields[`${prefix}_source_url`],
          errors: sourceValid ? [] : [sourceMessage]
        }
        newState.fields[`${prefix}_concept_url`] = {
          ...newState.fields[`${prefix}_concept_url`],
          errors: (!sourceValid || conceptValid) ? [] : [conceptMessage]
        }
      })
      return newState
    })
    return validity.every(({sourceValid, conceptValid}) => sourceValid && conceptValid)
  }

  onSubmit = event => {
    event.preventDefault();
    event.stopPropagation();

    const { fields } = this.state;
    const { edit } = this.props

    const isValid = this.setAllFieldsErrors()
    const isConceptRefValid = this.setConceptRefErrors()

    if(isValid && isConceptRefValid) {
      const payload = this.getValues()
      payload.extras = arrayToObject(fields.extras)
      if(edit)
        payload.update_comment = fields.comment.value
      if(!payload.sort_weight)
        delete payload.sort_weight
      OPTIONAL_BLANK_FIELDS.forEach(field => { if(!payload[field]) delete payload[field] })
      GAService.recordUpsertEvent('Mapping', edit)

      let service = APIService.new().overrideURL(this.props.source.url).appendToUrl('mappings/')
      service = edit ? service.appendToUrl(this.state.fields.id.value + '/').put(payload) : service.post(payload)
      service.then(this.handleSubmitResponse)
    }
  }

  handleSubmitResponse = response => {
    const { edit, t, onClose } = this.props
    const { setAlert } = this.context;

    if(response.status === 201 || response.status === 200) { // success
      setAlert({duration: 2000, message: edit ? t('mapping.success_update') : t('mapping.success_create'), severity: 'success'})
      onClose(response.data);
      window.location.hash = response.data.url
    } else if (response?.status === 208) {
      let error = get(response?.data, '__all__.0') || t('common.already_exists')
      setAlert({duration: 10000, message: `${response.status}: ${error}`, severity: 'error'})
    } else { // error
      let error = compact(flatten(values(response)))
      let field = get(keys(response), 0)
      if(isArray(error) && error[0] && field)
        error = error[0]
      setAlert({duration: 10000, message: `${response.status || field || "Error"}: ${error || response.data?.error || response.data?.detail || (edit ? t("mapping.error_update") : t("mapping.error_create"))}`, severity: 'error'})
    }
  }

  toggleManualMnemonic = () => {
    const newManualMnemonic = !this.state.manualMnemonic
    const newState = {...this.state}
    const autoAssignedId = Boolean(this.props.source?.autoid_mapping_mnemonic) && !newManualMnemonic
    newState.fields.id = {
      ...newState.fields.id,
      value: newManualMnemonic ? newState.fields.id.value : '',
      validators: autoAssignedId ? [matchPattern(ID_REGEX)] : [required(), matchPattern(ID_REGEX)],
      errors: []
    }
    newState.manualMnemonic = newManualMnemonic
    this.setState(newState)
  }

  toggleManualExternalId = () => {
    const newManualExternalId = !this.state.manualExternalId
    const newState = {...this.state}
    newState.fields.external_id = {...newState.fields.external_id, value: newManualExternalId ? newState.fields.external_id.value : ''}
    newState.manualExternalId = newManualExternalId
    this.setState(newState)
  }

  render() {
    const { fields, mapTypes, manualMnemonic, manualExternalId, mapping, advanced, fromConcept, toConcept, fromSource, toSource } = this.state;
    const { onClose, edit, source, t, repo } = this.props;
    const suggestedSources = compact([source]);
    return (
      <div className='col-xs-12' style={{padding: '8px 16px 12px 16px', height: '100%', overflow: 'auto'}}>
        <div className='col-xs-12 padding-0' style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px'}}>
          <span>
            <Breadcrumbs
              ownerURL={repo.owner_url}
              owner={repo.owner}
              ownerType={repo.owner_type}
              repo={repo.id}
              repoType={repo.type}
              id={mapping?.id || fields.id.value || '[mapping-id]'}
              repoURL={repo?.url}
              mapping
            />
          </span>
          <span>
            <CloseIconButton color='secondary' onClick={onClose} />
          </span>
        </div>
        <CardSection title={t('mapping.form.mapping_details.header')} sx={{p: 2, marginTop: 0}}>
          <div className='col-xs-12 padding-0' style={{marginTop: '24px'}}>
            {
              !edit &&
                <div style={{width: '100%'}}>
                  {
                    get(source, 'autoid_mapping_mnemonic') && !manualMnemonic ?
                      <span style={{fontWeight: '500', borderLeft: '3px solid lightgray', padding: '10px 5px'}}>
                        <Trans t={t} i18nKey='mapping.form.id_auto_assign' components={{manual: <a style={ANCHOR_UNDERLINE_STYLES} onClick={this.toggleManualMnemonic} />}} />
                      </span> :
                    <TextField
                      error={Boolean(fields.id.errors[0])}
                      id="id"
                      label={t('mapping.form.id')}
                      helperText={fields.id.errors[0] || this.getIdHelperText()}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.id.value}
                      disabled={edit}
                    />
                  }
                </div>
            }
            <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
              <Autocomplete
                disableClearable
                openOnFocus
                isOptionEqualToValue={(option, value) => option.id === value}
                value={fields.map_type.value}
                id="map_type"
                options={mapTypes}
                getOptionLabel={(option) => option?.name || option || ''}
                fullWidth
                required
                renderInput={
                  params => <TextField
                                {...params}
                                error={Boolean(fields.map_type.errors[0])}
                                required
                                label={t('mapping.map_type')}
                                variant="outlined"
                                fullWidth
           />
                }
                onChange={(event, item) => this.onAutoCompleteChange('map_type', item)}
              />
            </div>
            <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
              {
                get(source, 'autoid_mapping_external_id') && !manualExternalId && !edit ?
                  <span style={{fontWeight: '500', borderLeft: '3px solid lightgray', padding: '10px 5px'}}>
                    <Trans t={t} i18nKey='mapping.form.external_id_auto_assign' components={{manual: <a style={ANCHOR_UNDERLINE_STYLES} onClick={this.toggleManualExternalId} />}} />
                  </span> :
                <TextField
                  id="external_id"
                  label={t('common.external_id')}
                  variant="outlined"
                  fullWidth
                  onChange={this.onTextFieldChange}
                  value={fields.external_id.value}
                  helperText={edit ? undefined : this.getExternalIdHelperText()}
                />
              }
            </div>
            <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
              <TextField
                error={Boolean(fields.sort_weight.errors[0])}
                id="sort_weight"
                label={t('mapping.sort_weight')}
                variant="outlined"
                fullWidth
                onChange={this.onTextFieldChange}
                value={fields.sort_weight.value}
                helperText={fields.sort_weight.errors[0]}
              />
            </div>
          </div>
        </CardSection>
        <div className='col-xs-12 padding-0' style={{margin: '16px 0 8px 0', width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
          <MuiButton
            id='advanced'
            size='small'
            color='primary'
            variant={advanced ? 'contained' : 'text'}
            onClick={this.toggleAdvanced}
            sx={{textTransform: 'none'}}
          >
            {t('common.advanced')}
          </MuiButton>
        </div>
        <CardSection title={t('mapping.form.from_concept.header')}>
          {
            advanced ?
              <React.Fragment>
                <div className='col-xs-12 padding-0' style={{marginTop: '24px', width: '100%'}}>
                  <div className='col-xs-8 padding-left-0'>
                    <TextField
                      id="from_source_url"
                      label={t('mapping.from_source_url')}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.from_source_url.value}
                    />
                  </div>
                  <div className='col-xs-4 padding-0'>
                    <TextField
                      id="from_source_version"
                      label={t('mapping.from_source_version')}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.from_source_version.value}
                    />
                  </div>
                </div>
                <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
                  <TextField
                    id="from_concept_url"
                    label={t('mapping.from_concept_url')}
                    variant="outlined"
                    fullWidth
                    onChange={this.onTextFieldChange}
                    value={fields.from_concept_url.value}
                    error={Boolean(fields.from_concept_url.errors[0])}
                    helperText={fields.from_concept_url.errors[0]}
                  />
                </div>
                <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
                  <div className='col-xs-8 padding-left-0'>
                    <TextField
                      id="from_concept_name"
                      label={t('mapping.from_concept_name')}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.from_concept_name.value}
                    />
                  </div>
                  <div className='col-xs-4 padding-0'>
                    <TextField
                      id="from_concept_code"
                      label={t('mapping.from_concept_code')}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.from_concept_code.value}
                    />
                  </div>
                </div>
              </React.Fragment> :
            <React.Fragment>
              <div className='col-xs-12 padding-0' style={{marginTop: '24px', width: '100%'}}>
                <SourceSearchAutocomplete
                  required
                  id='from_source'
                  label={t('mapping.from_source')}
                  suggested={suggestedSources}
                  value={fromSource}
                  error={fields.from_source_url.errors[0]}
                  helperText={fields.from_source_url.errors[0]}
                  onChange={(id, item) => this.onSourceSelect('from', item)}
                />
              </div>
              <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
                <ConceptSearchAutocomplete
                  required
                  id='from_concept'
                  label={t('mapping.from_concept')}
                  disabled={!fromSource}
                  parentURI={fromSource?.url}
                  value={fromConcept}
                  error={fields.from_concept_url.errors[0]}
                  helperText={fields.from_concept_url.errors[0]}
                  onChange={(id, item) => this.onConceptSelect('from', item)}
                />
              </div>
            </React.Fragment>
          }
        </CardSection>
        <div className='col-xs-12 padding-0' style={{margin: '4px 0', width: '100%', textAlign: 'center'}}>
          <Tooltip arrow title={t('mapping.swap_concepts')}>
            <IconButton aria-label={t('mapping.swap_concepts')} color="primary" onClick={this.swapConcepts} size="large">
              <SwapIcon />
            </IconButton>
          </Tooltip>
        </div>
        <CardSection title={t('mapping.form.to_concept.header')}>
          {
            advanced ?
              <React.Fragment>
                <div className='col-xs-12 padding-0' style={{marginTop: '24px', width: '100%'}}>
                  <div className='col-xs-8 padding-left-0'>
                    <TextField
                      id="to_source_url"
                      label={t('mapping.to_source_url')}
                      placeholder={t('mapping.source_url_example')}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.to_source_url.value}
                    />
                  </div>
                  <div className='col-xs-4 padding-0'>
                    <TextField
                      id="to_source_version"
                      label={t('mapping.to_source_version')}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.to_source_version.value}
                    />
                  </div>
                </div>
                <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
                  <TextField
                    id="to_concept_url"
                    label={t('mapping.to_concept_url')}
                    variant="outlined"
                    fullWidth
                    onChange={this.onTextFieldChange}
                    value={fields.to_concept_url.value}
                    error={Boolean(fields.to_concept_url.errors[0])}
                    helperText={fields.to_concept_url.errors[0]}
                  />
                </div>
                <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
                  <div className='col-xs-8 padding-left-0'>
                    <TextField
                      id="to_concept_name"
                      label={t('mapping.to_concept_name')}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.to_concept_name.value}
                    />
                  </div>
                  <div className='col-xs-4 padding-0'>
                    <TextField
                      id="to_concept_code"
                      label={t('mapping.to_concept_code')}
                      variant="outlined"
                      fullWidth
                      onChange={this.onTextFieldChange}
                      value={fields.to_concept_code.value}
                    />
                  </div>
                </div>
              </React.Fragment> :
            <React.Fragment>
              <div className='col-xs-12 padding-0' style={{marginTop: '24px', width: '100%'}}>
                <SourceSearchAutocomplete
                  required
                  id='to_source'
                  label={t('mapping.to_source')}
                  suggested={suggestedSources}
                  value={toSource}
                  error={fields.to_source_url.errors[0]}
                  helperText={fields.to_source_url.errors[0]}
                  onChange={(id, item) => this.onSourceSelect('to', item)}
                />
              </div>
              <div className='col-xs-12 padding-0' style={{marginTop: '16px', width: '100%'}}>
                <ConceptSearchAutocomplete
                  required
                  id='to_concept'
                  label={t('mapping.to_concept')}
                  disabled={!toSource}
                  parentURI={toSource?.url}
                  value={toConcept}
                  error={fields.to_concept_url.errors[0]}
                  helperText={fields.to_concept_url.errors[0]}
                  onChange={(id, item) => this.onConceptSelect('to', item)}
                />
              </div>
            </React.Fragment>
          }
        </CardSection>
        <CardSection title={t('custom_attributes.label')}>
          <CustomAttributesForm extras={fields.extras} onChange={this.setExtrasValue} onAdd={this.onAddExtras} />
        </CardSection>

        {
          edit &&
            <CardSection title={t('common.update_comment')}>
              <div className='col-xs-12 padding-0' style={{marginTop: '24px'}}>
                <TextField
                  id="comment"
                  label={t('common.comment')}
                  variant="outlined"
                  fullWidth
                  onChange={event => this.setFieldValue('comment', event.target.value || '')}
                  value={fields.comment.value}
                  required
                  rows={3}
                  maxRows={4}
                  multiline
                  helperText={fields.comment.errors[0]}
                  error={Boolean(fields.comment.errors[0])}
                />

              </div>
            </CardSection>
        }
        <div className='col-xs-12 padding-0' style={{marginTop: '16px'}}>
          <Button label={t('common.submit')} sx={{backgroundColor: 'surface.s90'}} onClick={this.onSubmit} />
        </div>
      </div>
    );
  }
}

export default MappingForm;
